import React, { FC } from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  InputAdornment,
  MenuItem,
  TextField,
  Typography
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { AnimalType } from '../../../../../models/animal';
import { DiseaseViewModel } from '../../../../../models/injection';
import { AnimalTypeSelect, FarmDiseaseSelect } from '../../../../../elements';
import { WindSensitivity, windSensitivities } from '../../../../../models/notification-rule';
import { Skeleton } from '../../../../../elements/ui/skeleton';

export interface ILungWeatherTabProps {
  isDisabled?: boolean;
  animalType: AnimalType;
  setAnimalType: React.Dispatch<React.SetStateAction<AnimalType>>;
  farmDiseaseId: string;
  setFarmDiseaseId: React.Dispatch<React.SetStateAction<string>>;
  farmDiseases: DiseaseViewModel[];
  loadingDiseases: boolean;
  isPregnant?: boolean;
  isLactating?: boolean;
  setIsPregnant: React.Dispatch<React.SetStateAction<boolean | undefined>>;
  setIsLactating: React.Dispatch<React.SetStateAction<boolean | undefined>>;
  peakThreshold: string;
  setPeakThreshold: React.Dispatch<React.SetStateAction<string>>;
  lookbackDays: string;
  setLookbackDays: React.Dispatch<React.SetStateAction<string>>;
  forecastDays: string;
  setForecastDays: React.Dispatch<React.SetStateAction<string>>;
  sensitivity: WindSensitivity;
  setSensitivity: React.Dispatch<React.SetStateAction<WindSensitivity>>;
}

/**
 * Placeholder that stands in for the form while the diseases load.
 *
 * It repeats the real grid geometry - two fields per row, the last two rows carrying a helper line - so the
 * fields do not jump sideways or upwards once the data arrives.
 */
const LungWeatherRuleTabSkeleton: FC = () => (
  <Grid container spacing={1} alignItems="flex-start">
    {[false, true, true].map((withHelperText, row) => (
      <Grid item container spacing={1} alignItems="flex-start" key={row}>
        {[0, 1].map((column) => (
          <Grid item xs={12} sm={12} md={6} key={column}>
            <Skeleton className="h-10 w-full" />
            {withHelperText && <Skeleton className="mt-2 ml-3 h-3 w-3/4" />}
          </Grid>
        ))}
      </Grid>
    ))}
  </Grid>
);

/**
 * Configures a lung-antibiotic peak / weather correlation rule.
 *
 * There is deliberately no medication field, which is why the region type toggle is hidden for this tab. The
 * medication that counts towards a peak is the one configured on the disease itself, so offering a second place
 * to set it would only invite disagreement between the two.
 */
const LungWeatherRuleTab: FC<ILungWeatherTabProps> = (props) => {
  const { t } = useTranslation();

  const {
    isDisabled,
    animalType,
    setAnimalType,
    farmDiseaseId,
    setFarmDiseaseId,
    farmDiseases,
    loadingDiseases,
    isPregnant,
    isLactating,
    setIsPregnant,
    setIsLactating,
    peakThreshold,
    setPeakThreshold,
    lookbackDays,
    setLookbackDays,
    forecastDays,
    setForecastDays,
    sensitivity,
    setSensitivity
  } = props;

  return (
    <Box p={1}>
      <Box mb={2} className="rounded-lg border border-blue-100 bg-blue-50 p-3">
        <Typography variant="body2" color="textSecondary">
          {t('f-notif-rule-lung-weather-hint')}
        </Typography>
      </Box>

      {/* Rows top-align rather than centre: the fields carry helper text of differing heights, and centring
          would offset the field boxes against each other by half that difference. */}
      {loadingDiseases ? (
        <LungWeatherRuleTabSkeleton />
      ) : (
        <Grid container spacing={1} alignItems="flex-start">
          <Grid item container spacing={1} alignItems="flex-start">
            <Grid item xs={12} sm={12} md={6}>
              <AnimalTypeSelect fullWidth value={animalType} onAnimalTypeChange={(a) => setAnimalType(a)} allowAny />
            </Grid>

            <Grid item xs={12} sm={12} md={6}>
              <FarmDiseaseSelect
                fullWidth
                disabled={isDisabled}
                diseases={farmDiseases}
                value={farmDiseaseId}
                onSupplyChange={setFarmDiseaseId}
              />
            </Grid>
          </Grid>

          {/* Threshold and timescale read as one sentence - "this many administrations within this many days" -
            so they sit on one row rather than in separate groups. */}
          <Grid item container spacing={1} alignItems="flex-start">
            <Grid item xs={12} sm={12} md={6}>
              <TextField
                label={t('f-notif-rule-peak-threshold')}
                variant="outlined"
                fullWidth
                size="small"
                type="number"
                disabled={isDisabled}
                value={peakThreshold}
                onChange={(e) => setPeakThreshold(e.target.value)}
                helperText={t('f-notif-rule-peak-threshold-hint')}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={6}>
              <TextField
                label={t('f-notif-rule-lookback-days')}
                variant="outlined"
                fullWidth
                size="small"
                type="number"
                disabled={isDisabled}
                value={lookbackDays}
                onChange={(e) => setLookbackDays(e.target.value)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">{t('f-day-s')}</InputAdornment>
                }}
                helperText={t('f-notif-rule-lookback-days-hint')}
              />
            </Grid>
          </Grid>

          {(animalType === AnimalType.Sow || animalType === AnimalType.Gilt) && (
            <Grid item container spacing={1} alignItems="center">
              <Grid item xs={6} sm={6} md={3}>
                <FormControlLabel
                  label={t('f-animal-preg-only')}
                  control={
                    <Checkbox
                      color="primary"
                      disabled={isDisabled}
                      checked={isPregnant || false}
                      onChange={(e) => setIsPregnant(e.target.checked)}
                    />
                  }
                />
              </Grid>
              <Grid item xs={6} sm={6} md={3}>
                <FormControlLabel
                  label={t('f-animal-is-lact')}
                  control={
                    <Checkbox
                      color="primary"
                      disabled={isDisabled}
                      checked={isLactating || false}
                      onChange={(e) => setIsLactating(e.target.checked)}
                    />
                  }
                />
              </Grid>
            </Grid>
          )}

          <Grid item container spacing={1} alignItems="flex-start">
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                label={t('f-notif-rule-forecast-days')}
                variant="outlined"
                fullWidth
                size="small"
                type="number"
                disabled={isDisabled}
                value={forecastDays}
                onChange={(e) => setForecastDays(e.target.value)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">{t('f-day-s')}</InputAdornment>
                }}
                helperText={t('f-notif-rule-forecast-days-hint')}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <TextField
                select
                label={t('f-notif-rule-sensitivity')}
                variant="outlined"
                fullWidth
                size="small"
                disabled={isDisabled}
                value={sensitivity}
                onChange={(e) => setSensitivity(e.target.value as WindSensitivity)}
                helperText={t(`f-notif-rule-sensitivity-hint-${sensitivity.toLowerCase()}`)}
              >
                {windSensitivities.map((option) => (
                  <MenuItem key={option} value={option}>
                    {t(`f-notif-rule-sensitivity-${option.toLowerCase()}`)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default LungWeatherRuleTab;
